import React from "react";
import {DateInput} from "@/shared/components/ui/date-input";
import {Button} from "@/shared/components/ui/button";
import {Input} from "@/shared/components/ui/input";
import {Search} from "lucide-react";
import {ClientCRM, VehicleType, getClientIdentLabel} from "@/features/types/StatusChangeInterface";
import {Row} from "@tanstack/react-table";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";
import {ModalSectionItemProps} from "@/shared/components/ModalSection";

export interface OrderActionContentDataProps {
    row: Row<AutoTableInterface>;
    client: ClientCRM | null;
    onShowClientSearch: () => void;
    ord_vehicle_type: VehicleType | null;
    onVehicleTypeChange: (type: VehicleType) => void;
    ord_sales_agr_issue_dat: Date | null;
    onIssueDateChange: (date: Date | null) => void;
    ord_sales_agr_registr_dat: Date | null;
    onRegistrDateChange: (date: Date | null) => void;
    ord_sales_price: string;
    onSalesPriceChange: (price: string) => void;
}

export const getOrderActionContentSections = (props: OrderActionContentDataProps): ModalSectionItemProps[] => {
    const {
        row,
        client,
        onShowClientSearch,
        ord_vehicle_type,
        onVehicleTypeChange,
        ord_sales_agr_issue_dat,
        onIssueDateChange,
        ord_sales_agr_registr_dat,
        onRegistrDateChange,
        ord_sales_price,
        onSalesPriceChange,
    } = props;

    return [
        {
            title: "Dane pojazdu",
            state: "always-open",
            content: (
                <div className="text-xs lg:text-sm">
                    <table className="w-full border-collapse text-left [&_th]:text-xs [&_th]:lg:text-sm [&_th]:font-medium [&_td]:text-xs [&_td]:lg:text-sm">
                        <tbody>
                        <tr>
                            <th>Auto</th>
                            <th>Nr. zam.</th>
                            <th>Data zam.</th>
                            <th>Dni na Stocku</th>
                        </tr>
                        <tr>
                            <td>{(row.original.f_veh_make && row.original.f_veh_desc_1) ? `${row.original.f_veh_make} ${row.original.f_veh_desc_1}` : "-"}</td>
                            <td>{row.original.f_ord_num || "-"}</td>
                            <td>{row.original.f_ord_d || "-"}</td>
                            <td>{row.original.f_supp_2_doc_invc_o_diff || "-"}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            ),
        },
        {
            title: `Klient${client?.f_crm_id ? ` (ID: ${client?.f_crm_id})` : ''}`,
            state: "collapsible-open",
            content: (
                <div className="text-xs lg:text-sm">
                    <table
                        className="border-collapse text-left ml-4 [&_th]:text-xs [&_th]:lg:text-sm [&_th]:font-medium [&_th]:pr-3 [&_th]:py-0.5 [&_td]:py-0.5 [&_td]:align-middle [&_td]:text-xs [&_td]:lg:text-sm [&_td]:font-small">
                        <tbody>
                        <tr>
                            <th>Typ klienta:</th>
                            <td>{client?.f_crm_c_typ_desc || "-"}</td>
                        </tr>
                        <tr>
                            <th>Skrót nazwy:</th>
                            <td>{client?.f_crm_o_name || "-"}</td>
                        </tr>
                        <tr>
                            <th>{getClientIdentLabel(client?.f_crm_c_typ_desc)}</th>
                            <td>{client?.f_crm_r_clt_ident || "-"}</td>
                        </tr>
                        <tr>
                            <th>Adres:</th>
                            <td>{client?.f_crm_r_adr_2 || "-"}</td>
                        </tr>
                        <tr>
                            <th>Miasto:</th>
                            <td>{client?.f_crm_l_cty || "-"}</td>
                        </tr>
                        <tr>
                            <th>Osoba kontaktowa:</th>
                            <td>{client?.f_crm_z_clt || "-"}</td>
                        </tr>
                        <tr>
                            <th>Tel. kom.:</th>
                            <td>{client?.f_crm_r_phone_1 || "-"}</td>
                        </tr>
                        </tbody>
                    </table>
                    <div className="flex justify-end mt-0.5">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onShowClientSearch}
                            className="h-8 text-xs lg:text-sm"
                        >
                            <Search size={14} className="mr-1"/>
                            Wyszukaj klienta (CRM)
                        </Button>
                    </div>
                </div>
            ),
        },
        {
            title: "Pojazd i daty",
            state: "collapsible-closed",
            content: (
                <div className="text-xs lg:text-sm">
                    <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                            <div className="text-left font-medium mb-0.5">
                                Typ pojazdu:
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="ord_vehicle_type"
                                        value="Osobowy"
                                        checked={ord_vehicle_type === "Osobowy"}
                                        onChange={(e) => onVehicleTypeChange(e.target.value as VehicleType)}
                                        className="cursor-pointer"
                                    />
                                    <span>Osobowy</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="ord_vehicle_type"
                                        value="Ciężarowy"
                                        checked={ord_vehicle_type === "Ciężarowy"}
                                        onChange={(e) => onVehicleTypeChange(e.target.value as VehicleType)}
                                        className="cursor-pointer"
                                    />
                                    <span>Ciężarowy</span>
                                </label>
                            </div>
                        </div>
                        <div className="flex-1">
                            <table
                                className="w-full border-collapse text-left [&_th]:text-xs [&_th]:lg:text-sm [&_th]:font-medium [&_th]:whitespace-nowrap [&_th]:pr-3 [&_th]:pt-0 [&_th]:pb-0.5 [&_td]:w-full [&_td]:pt-0 [&_td]:pb-0.5 [&_td]:align-top [&_td]:text-xs [&_td]:lg:text-sm">
                                <tbody>
                                <tr>
                                    <th>Planowana data wydania:</th>
                                    <td>
                                        <div className="[&_*]:text-xs [&_*]:lg:text-sm">
                                            <DateInput
                                                value={ord_sales_agr_issue_dat}
                                                onChange={onIssueDateChange}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Planowana data rejestrowania:</th>
                                    <td>
                                        <div className="[&_*]:text-xs [&_*]:lg:text-sm">
                                            <DateInput
                                                value={ord_sales_agr_registr_dat}
                                                onChange={onRegistrDateChange}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Prognozowana marża netto:</th>
                                    <td>
                                        <Input
                                            type="text"
                                            value={ord_sales_price}
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                if (/^[0-9]*$/.test(next)) {
                                                    onSalesPriceChange(next);
                                                }
                                            }}
                                            className="w-full text-xs lg:text-sm"
                                        />
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ),
        },
    ];
};

